import React, { useRef, useCallback } from "react";
import Grid from "@mui/system/Grid";
import { Box } from "@mui/material";
import { motion } from "framer-motion";

import VideoPlayPausebutton from "./components/VideoPlayPauseButton";
import VideoTrimButton from "../VideoEditor/components/VideoTrimButton";
import VideoEditorStartTimeInput from "../VideoEditor/components/VideoEditorStartTimeInput";
import VideoEditorEndTimeInput from "../VideoEditor/components/VideoEditorEndTimeInput";
import VideoCutButton from "../VideoEditor/components/VideoCutButton";
import VideoDownloadButton from "../VideoEditor/components/VideoDownloadButton";

// 1. Import the Autonomous Brain
import { useKernel } from "../../hooks/AIKernel"; // Adjust relative path as needed

// Create a motion-enabled Box for safe MUI + Framer Motion integration
const MotionBox = motion(Box);

const VideoPlayerControls: React.FC = () => {
  // 2. Connect to the Agent's state and sensory inputs
  const { agentState, reportBehavior } = useKernel();

  // 3. Adaptive Theme Variables
  const isDarkMode = agentState?.theme === "theme-cinematic-dark";
  const highlightCTA = agentState?.ui?.highlightCTAs;
  
  const bgColor = isDarkMode ? "rgba(18, 18, 18, 0.8)" : "rgba(255, 255, 255, 0.8)";
  const borderColor = isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)";

  // 4. TELEMETRY: Track hesitation / dwell time over the controls
  const hoverStartTime = useRef<number>(0);

  const handleMouseEnter = useCallback(() => {
    hoverStartTime.current = Date.now();
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (hoverStartTime.current > 0) {
      const dwellTime = (Date.now() - hoverStartTime.current) / 1000;
      
      // If they hover over the controls for more than 4 seconds without taking action, log hesitation
      if (dwellTime > 4 && reportBehavior) {
        reportBehavior("control_hesitation", 1, "behavior");
      }
      hoverStartTime.current = 0;
    }
  }, [reportBehavior]);

  return (
    <MotionBox
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      layout // AUTONOMOUS MOVEMENT: Smoothly animates any layout shifts
      sx={{
        backgroundColor: bgColor,
        backdropFilter: "blur(10px)", // Glassmorphism effect
        borderTop: `1px solid ${borderColor}`,
        borderBottom: `1px solid ${borderColor}`,
        transition: "background-color 0.5s ease, border-color 0.5s ease",
        borderRadius: "8px",
        overflow: "hidden",
        mb: 1
      }}
    >
      <Grid
        container
        spacing={0}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 1,
          p: 1.5,
          width: "100%",
        }}
      >
        <Grid
          size={{ xs: 12, md: "grow" }}
          sx={{
            display: "flex",
            gap: 1.5,
            alignContent: "stretch",
            justifyContent: "flex-start",
            alignItems: "center"
          }}
        >
          <VideoPlayPausebutton sx={{ ml: 0, mr: "auto" }} />
          <VideoEditorStartTimeInput
            sx={{ maxWidth: "148px", "& input": { fontSize: "16px" } }}
          />
          <VideoEditorEndTimeInput
            sx={{ maxWidth: "148px", "& input": { fontSize: "16px" } }}
          />
        </Grid>
        
        {/* Action Buttons Group */}
        <Grid
          size={{ xs: 12, md: 4 }}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignContent: "stretch",
          }}
        >
          <MotionBox
            // AUTONOMOUS MOVEMENT: If the user is frustrated, these buttons will literally 
            // "breathe" and scale up autonomously to say "Click here next!"
            animate={
              highlightCTA
                ? { 
                    y: [0, -3, 0], 
                    scale: [1, 1.02, 1],
                    boxShadow: isDarkMode 
                      ? ["0px 0px 0px rgba(0,229,255,0)", "0px 4px 12px rgba(0,229,255,0.2)", "0px 0px 0px rgba(0,229,255,0)"]
                      : ["0px 0px 0px rgba(0,0,0,0)", "0px 4px 12px rgba(0,0,0,0.1)", "0px 0px 0px rgba(0,0,0,0)"]
                  }
                : { y: 0, scale: 1, boxShadow: "none" }
            }
            transition={{ 
              duration: 2, 
              repeat: highlightCTA ? Infinity : 0, 
              ease: "easeInOut" 
            }}
            sx={{
              display: "flex",
              gap: 1,
              p: 0.5,
              borderRadius: "8px",
              "& button": {
                height: "100%",
              },
            }}
          >
            <VideoTrimButton />
            <VideoCutButton />
            <VideoDownloadButton />
          </MotionBox>
        </Grid>
      </Grid>
    </MotionBox>
  );
};

export default VideoPlayerControls;